"use client";

/**
 * Client provider for the reactions catalog. Built once from the SSR-loaded
 * list so all consumers (replacers, pickers, dual-pane editors) share the
 * same map and avoid re-running the work each render.
 *
 * Memory model:
 *   - `enabled` mirrors the global reactions kill-switch. When false,
 *     all helpers no-op and rendering falls back to the raw emoji text.
 *   - `byEmoji` is a `Map<string, ReactionDefinition>` keyed by the primary
 *     emoji AND every declared alias. That's why a one-line emoji like 😂
 *     can be transparently mapped to a custom laugh asset.
 */
import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useAudiences } from "@/lib/flags/client";
import { buildShortcodeMap, type ShortcodeReactionMap } from "./shortcodes";
import type {
  EmojiReactionMap,
  ReactionCatalogFilter,
  ReactionDefinition,
} from "./types";

interface ReactionsContextValue {
  enabled: boolean;
  list: readonly ReactionDefinition[];
  byEmoji: EmojiReactionMap;
  /** `smiley` → reacción, para `:smiley:` y `(smiley)`. */
  byShortcode: ShortcodeReactionMap;
}

const ReactionsContext = createContext<ReactionsContextValue | null>(null);

const EMPTY_LIST: readonly ReactionDefinition[] = Object.freeze([]);
const EMPTY_MAP: EmojiReactionMap = new Map();
const EMPTY_SHORTCODES: ShortcodeReactionMap = new Map();

export function ReactionsProvider({
  reactions,
  enabled,
  children,
}: {
  reactions: readonly ReactionDefinition[];
  enabled: boolean;
  children: ReactNode;
}) {
  const value = useMemo<ReactionsContextValue>(() => {
    if (!enabled) {
      return { enabled: false, list: EMPTY_LIST, byEmoji: EMPTY_MAP, byShortcode: EMPTY_SHORTCODES };
    }
    const byEmoji = new Map<string, ReactionDefinition>();
    for (const r of reactions) {
      if (r.emoji) byEmoji.set(r.emoji, r);
      for (const alias of r.aliases ?? []) {
        if (alias && !byEmoji.has(alias)) byEmoji.set(alias, r);
      }
    }
    return { enabled: true, list: reactions, byEmoji, byShortcode: buildShortcodeMap(reactions) };
  }, [enabled, reactions]);

  return (
    <ReactionsContext.Provider value={value}>
      {children}
    </ReactionsContext.Provider>
  );
}

export function useReactionsCatalog(): ReactionsContextValue {
  const ctx = useContext(ReactionsContext);
  return ctx ?? { enabled: false, list: EMPTY_LIST, byEmoji: EMPTY_MAP, byShortcode: EMPTY_SHORTCODES };
}

export function useReactions(filter: ReactionCatalogFilter = {}) {
  const ctx = useReactionsCatalog();
  const audiences = useAudiences();
  const list = useMemo(() => {
    if (!ctx.enabled) return EMPTY_LIST;
    return filterReactionsClient(ctx.list, {
      ...filter,
      audiences: filter.audiences ?? audiences,
    });
  }, [
    audiences,
    ctx.enabled,
    ctx.list,
    filter.audiences,
    filter.contentType,
    filter.module,
    filter.page,
    filter.scope,
  ]);
  return { ...ctx, list };
}

/** Lookup a reaction by emoji glyph (primary or alias). */
export function useReactionForEmoji(emoji: string): ReactionDefinition | null {
  const { byEmoji } = useReactionsCatalog();
  return byEmoji.get(emoji) ?? null;
}

function filterReactionsClient(
  list: readonly ReactionDefinition[],
  filter: ReactionCatalogFilter
): readonly ReactionDefinition[] {
  return list.filter((reaction) => {
    if (filter.scope && filter.scope !== "ALL") {
      if (reaction.scope !== filter.scope && reaction.scope !== "BOTH") return false;
    }
    if (!matchesList(reaction.modules, filter.module, "global")) return false;
    if (!matchesPage(reaction.pages, filter.page)) return false;
    if (!matchesList(reaction.contentTypes, filter.contentType)) return false;
    if (!matchesAudience(reaction.audiences, filter.audiences ?? [])) return false;
    return true;
  });
}

function matchesList(
  configured: readonly string[] | null | undefined,
  requested: string | undefined,
  globalToken?: string
): boolean {
  if (!configured?.length) return true;
  if (globalToken && configured.includes(globalToken)) return true;
  if (!requested) return true;
  return configured.includes(requested);
}

function matchesPage(
  configured: readonly string[] | null | undefined,
  page: string | undefined
): boolean {
  if (!configured?.length) return true;
  if (!page) return true;
  return configured.some((entry) => {
    if (entry === "*" || entry === page) return true;
    if (entry.endsWith("*")) return page.startsWith(entry.slice(0, -1));
    return false;
  });
}

function matchesAudience(
  configured: readonly string[] | null | undefined,
  active: readonly string[]
): boolean {
  if (!configured?.length || configured.includes("all")) return true;
  if (active.length === 0) return false;
  return configured.some((token) => active.includes(token));
}
